import { useEffect } from "react";

/* ── The three numbers every guided-room screen lays out against ────────
   `--sat` / `--sab`  the notch and home-indicator insets
   `--kb`             how much of the viewport the on-screen keyboard eats
   plus `html[data-kb="on"|"off"]`, so layout reacts in CSS and no React
   component re-renders while someone types.

   Written to documentElement, never to the page root: the device-preview
   harness loads these screens in an iframe where `env(safe-area-inset-*)`
   is always 0 — a phone's insets can only be simulated from outside. With
   `?safe=1&st=59&sb=34` the harness says what an iPhone would report.

   Spec: docs/product specs/responsive/RESPONSIVE_SPEC.md §2 and §3. */

const isDevPreviewAllowed = () =>
  process.env.NODE_ENV !== "production" ||
  /(^|\.)stage\.openskyvr\.com$/.test(window.location.hostname) ||
  /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);

export const useViewportInsets = (): void => {
  useEffect(() => {
    const root = document.documentElement;
    const q = new URLSearchParams(window.location.search);

    /* Safe areas. The CSS default is env(); this only overrides it when the
       harness asks, and only outside production hosts. */
    if (q.get("safe") === "1" && isDevPreviewAllowed()) {
      const px = (v: string | null) => `${Math.max(0, Number(v) || 0)}px`;
      root.style.setProperty("--sat", px(q.get("st")));
      root.style.setProperty("--sab", px(q.get("sb")));
    }

    /* Keyboard.

       A threshold, not "greater than zero". `innerHeight` and
       `visualViewport.height` disagree by a fraction of a pixel on a
       Windows desktop at 125% display scaling, and by a few pixels
       wherever a browser rounds differently — so "> 0" reported a
       keyboard on machines that have none, and every screen rendered its
       keyboard-open layout permanently. On the join page that pinned the
       form to the top of a tall column with a large empty band beneath
       it, which read as a layout bug and was chased as one.

       No on-screen keyboard is 120px tall; the smallest (an iPhone SE
       number pad in landscape) is over 200. Anything under this is
       rounding, a toolbar, or a scrollbar — none of which should move the
       layout. */
    const KB_MIN = 120;
    const set = (v: number) => {
      const kb = v >= KB_MIN ? v : 0;
      root.style.setProperty("--kb", `${kb}px`);
      root.dataset.kb = kb > 0 ? "on" : "off";
    };

    if (q.has("kb") && isDevPreviewAllowed()) {
      set(Number(q.get("kb")) || 0);
      return undefined;
    }

    const vv = window.visualViewport;
    if (!vv) {
      /* No visualViewport (older Android WebViews): the keyboard is
         invisible to us, so claim none rather than guess at one. */
      set(0);
      return undefined;
    }

    const update = () => set(Math.max(0, window.innerHeight - vv.height - vv.offsetTop));
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    update();
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
    };
  }, []);
};

export default useViewportInsets;
